import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ops from "../../styles/opsConsole.module.css";
import VendorActivity from "./VendorActivity";
import VendorSection from "./VendorSection";
import css from "./Vendors.module.css";
import MarginAgreement from "./MarginAgreement";
import { Kpi, Tile, IcoStore, IcoCheck, IcoInbox, IcoWarn } from "../../styles/opsBits";
import {
  Bank,
  Cal,
  Copy,
  ErrorCard,
  External,
  FileOff,
  Globe,
  Mail,
  Phone,
  Receipt,
  SkeletonRows,
  SortTh,
  Th,
  Value,
  VerificationBadge,
} from "./VendorBits";
import {
  LISTING_SORTS,
  OPEN_APPROVAL,
  STATUS_LABEL,
  ago,
  blanks,
  date,
  get,
  money,
  pct,
  post,
  shapeListing,
  sortRows,
  val,
} from "./vendorApi";

/**
 * THE VENDOR'S ZOHO BOOKS CONTACT — what every Purchase Order for this vendor
 * is raised against. Paste the id of a contact already in Books (Ingram Micro,
 * Cloud Generic…), or let the server find the matching Books vendor by GSTIN
 * and create one only when there is none.
 */
function BooksVendorCard({ vendorId, books, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [paste, setPaste] = useState("");
  const [note, setNote] = useState(null); // { ok, text }
  const linked = Boolean(books?.vendor_id);

  const run = async (payload) => {
    setBusy(true);
    setNote(null);
    try {
      const out = await post(`/admin/vendors/${vendorId}/books`, payload);
      setNote({ ok: true, text: out?.msg || "Done." });
      setPaste("");
      await onChanged();
    } catch (e) {
      setNote({ ok: false, text: e.message || "Zoho Books did not answer." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={`${ops.card} ${css.secPad}`}>
      <h3>
        Zoho Books
        <span className={`${css.hdrFlag} ${linked ? css.hdrFlagOk : css.hdrFlagWarn}`}>
          {linked ? "linked" : "not linked"}
        </span>
      </h3>
      <p className={css.subtle} style={{ fontSize: 12.5, margin: "4px 0 10px" }}>
        Every payout to this vendor raises a Purchase Order in Books against this
        vendor. {linked ? "" : "Until it is linked, those payouts wait without one."}
      </p>
      {linked && (
        <dl className={css.fields}>
          <dt>Books vendor ID</dt>
          <dd>
            <a href={books.link} target="_blank" rel="noreferrer" style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
              {books.vendor_id}
            </a>
          </dd>
        </dl>
      )}
      <div className={css.booksRow}>
        <input
          className={css.booksInput}
          placeholder={linked ? "Replace with another Books vendor ID" : "Paste a Books vendor ID"}
          value={paste}
          onChange={(e) => setPaste(e.target.value.trim())}
          disabled={busy}
        />
        <MuiButton
          size="small"
          variant="outlined"
          disabled={busy || !paste}
          onClick={() => run({ books_vendor_id: paste })}
        >
          Link
        </MuiButton>
        {!linked && (
          <MuiButton size="small" variant="contained" disabled={busy} onClick={() => run({})}>
            Find or create in Books
          </MuiButton>
        )}
        {linked && (
          <MuiButton size="small" color="warning" disabled={busy} onClick={() => run({ unlink: true })}>
            Unlink
          </MuiButton>
        )}
      </div>
      {note && (
        <p
          className={css.subtle}
          style={{ fontSize: 12.5, marginTop: 8, color: note.ok ? "var(--ok, #15803d)" : "var(--danger, #b91c1c)" }}
        >
          {note.text}
        </p>
      )}
    </section>
  );
}

/**
 * THE VENDOR RECORD
 * =================
 * Built around the six questions an operator opens this page to answer, in
 * the order they ask them:
 *
 *   who is this  →  identity block
 *   are they cleared to trade  →  the badge and the decision buttons
 *   what is outstanding  →  "Needs attention", pinned above everything
 *   what do they sell  →  the listings table
 *   what is the commercial deal  →  the margin panel
 *   what can I do about it  →  the actions, always in the same place
 *
 * The record is mostly EMPTY for most vendors: the API returns clean empty
 * strings for every tax and bank field where the underlying pipe-joined
 * columns hold nothing. Absence is the common case here, not the exception,
 * and every field is designed for it.
 * A wall of em-dashes reads as a page that failed to load; "Not provided",
 * with the section saying what is missing and what to do, reads as a fact
 * about the vendor.
 */

const TAX_FIELDS = [
  ["gst", "GSTIN", true],
  ["pan", "PAN", true],
  ["cin", "CIN", true],
  ["tax_id", "Other tax ID", true],
];

const BANK_FIELDS = [
  ["holder", "Account holder", false],
  ["account", "Account no.", true],
  ["bank", "Bank", false],
  ["ifsc", "IFSC", true],
  ["swift", "SWIFT", true],
];

/** Every section of the record, in page order — "expand all" opens these. */
const SECTION_IDS = [
  "profile",
  "agreement",
  "listings",
  "submissions",
  "payouts",
  "orders",
  "subscriptions",
  "customers",
];
const OPEN_KEY = "vendorDetail.openSections";

const APPROVAL_SORTS = {
  sku: (r) => String(r.sku_name || "").toLowerCase(),
  change: (r) => String(r.change_type || "").toLowerCase(),
  outcome: (r) => String(r.status || "").toLowerCase(),
  submitted: (r) => new Date(r.created_at || 0).getTime() || 0,
};

export default function VendorDetail({ id }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [asking, setAsking] = useState(null);
  const [copied, setCopied] = useState("");

  const [lSort, setLSort] = useState({ key: "updated", dir: "desc" });

  /* Which sections are open. Remembered across vendors on this browser: an
     operator who always reads the agreement first should not reopen it on
     every vendor. Storage can be unavailable; the page then just starts
     closed. */
  const [openSecs, setOpenSecs] = useState(() => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(OPEN_KEY) || "{}");
      return saved && typeof saved === "object" ? saved : {};
    } catch {
      return {};
    }
  });
  const setOpen = (next) => {
    setOpenSecs(next);
    try {
      window.localStorage.setItem(OPEN_KEY, JSON.stringify(next));
    } catch {
      /* not remembered — fine */
    }
  };
  const toggleSec = (k) => setOpen({ ...openSecs, [k]: !openSecs[k] });
  const allOpen = SECTION_IDS.every((k) => openSecs[k]);
  const [aSort, setASort] = useState({ key: "submitted", dir: "desc" });

  /** The activity sections want the rows, not the envelope. */
  const activityGet = useCallback(
    (path) => get(path).then((data) => (Array.isArray(data) ? data : [])),
    [],
  );

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    return get(`/admin/vendors/${id}`)
      .then((data) => {
        setD(data || null);
        if (!data) setError("That vendor does not exist, or you cannot see it.");
      })
      .catch((e) => {
        setD(null);
        setError(e.message || "Could not load the vendor.");
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Verifying and moving back to review both fire instantly and both are
  // felt outside this screen — verifying emails the vendor and lets their
  // listings publish, moving to review silently blocks publishing for a
  // vendor who was cleared. Neither should be one stray click away. Reject
  // already stops for a reason, which is its own confirmation.
  const [confirming, setConfirming] = useState(null);

  const CONFIRM_COPY = {
    verified: {
      title: "Mark this vendor verified?",
      body: "They are emailed that they passed, and their approved listings can go live to buyers from now on. You can move them back to review later.",
      action: "Mark verified",
    },
    pending: {
      title: "Move this vendor back to review?",
      body: "Verification is a gate, not a label: while they sit in review their listings cannot be published, including ones already approved. They are not emailed — this is a note to yourselves.",
      action: "Move to review",
    },
  };

  const decide = async (status) => {
    if (status === "rejected" && note.trim().length < 5) {
      setAsking("rejected");
      return;
    }
    setBusy(true);
    try {
      await post("/admin/set_vendor_status", {
        vendor_id: Number(id),
        status,
        note: note.trim(),
      });
      setNote("");
      setAsking(null);
      await load();
    } catch (e) {
      setError(e.message || "Could not update the vendor.");
    } finally {
      setBusy(false);
    }
  };

  const copy = (text, tag) => {
    if (!text) return;
    const done = () => {
      setCopied(tag);
      setTimeout(() => setCopied(""), 1600);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done, () => {});
    }
  };

  /* ── derived, before any early return so the hook order is stable ──── */

  const owned = useMemo(() => {
    const raw = Array.isArray(d?.listings) ? d.listings : [];
    /* The API is authoritative about which approval a listing points at, and
       a null there means "nothing was ever submitted" — it must be respected,
       not papered over. The derivation below only runs against an older build
       that never sent the field at all (undefined, not null), where the
       submission history is the only source: it is ordered newest first, so
       the first entry for a SKU is the one to open. */
    const bySku = {};
    const openBySku = {};
    (Array.isArray(d?.approvals) ? d.approvals : []).forEach((a) => {
      if (bySku[a.sku_id] === undefined) bySku[a.sku_id] = a.id;
      if (OPEN_APPROVAL.has(a.status)) openBySku[a.sku_id] = true;
    });
    return sortRows(
      raw.map((l) => {
        const row = shapeListing(l);
        if (row.approval_id !== undefined) return row;
        return {
          ...row,
          approval_id: bySku[row.id] ?? null,
          has_open_approval: Boolean(openBySku[row.id]),
        };
      }),
      LISTING_SORTS,
      lSort.key,
      lSort.dir
    );
  }, [d, lSort]);

  /* OWNED AND RESOLD, ONE LIST. A listing is a product this vendor created
     and governs (its content, its plan names). A resold product is someone
     else's — often ours — on which this vendor only prices plans. Both are
     what they sell, so both belong in the list, told apart by a role tag;
     a reseller like Ingram owns nothing and used to read as "has not listed
     anything yet". */
  const resold = useMemo(
    () =>
      (Array.isArray(d?.resold) ? d.resold : []).map((r) => ({
        ...shapeListing(r),
        role: "resell",
        plans_priced: Number(r.plans_priced || 0),
        plans_live: Number(r.plans_live || 0),
      })),
    [d]
  );
  const listings = useMemo(
    () =>
      sortRows(
        [...owned.map((l) => ({ ...l, role: "own" })), ...resold],
        LISTING_SORTS,
        lSort.key,
        lSort.dir
      ),
    [owned, resold, lSort]
  );

  /* Price/content submissions AND catalogue requests (a plan, a period, a
     rename). Requests live in their own table, so the history used to say
     "nothing ever submitted" about a reseller whose requests had been
     decided. */
  const approvals = useMemo(
    () =>
      sortRows(
        [
          ...(Array.isArray(d?.approvals) ? d.approvals : []).map((a) => ({
            ...a,
            kind: "submission",
          })),
          ...(Array.isArray(d?.catalog_requests) ? d.catalog_requests : []).map((r) => ({
            id: `req-${r.id}`,
            proposal_id: r.id,
            sku_id: r.sku_id,
            sku_name: r.sku_name,
            change_type: "catalogue request",
            summary: r.summary,
            status: r.status,
            decision_note: r.decision_note,
            created_at: r.created_at,
            kind: "request",
          })),
        ],
        APPROVAL_SORTS,
        aSort.key,
        aSort.dir
      ),
    [d, aSort]
  );

  if (loading && !d) {
    return (
      <div className={`${ops.root} ${css.page}`}>
        <div className={ops.card}>
          <div className={css.tableWrap}>
            <table className={ops.table}>
              <tbody>
                <SkeletonRows cols={3} rows={6} />
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  if (error && !d) {
    return (
      <div className={`${ops.root} ${css.page}`}>
        <BackLink navigate={navigate} />
        <ErrorCard message={error} onRetry={load} />
      </div>
    );
  }
  if (!d) return null;

  const v = d.verification?.status || "unverified";
  const p = d.profile || {};
  const tax = d.tax || {};
  const bank = d.bank || {};
  const com = d.commercials || {};

  const taxEmpty = blanks(tax, ["gst", "pan", "cin", "tax_id"]);
  const bankEmpty = blanks(bank, ["holder", "account", "bank", "ifsc", "swift"]);

  const live = owned.filter((l) => String(l.status).toLowerCase() === "active").length;
  const resoldLive = resold.filter((r) => r.plans_live > 0).length;
  const marginSet =
    (com.margin_rate !== null && com.margin_rate !== undefined) ||
    (com.margin_flat_cut !== null && com.margin_flat_cut !== undefined);
  const openReviews = approvals.filter((a) => a.status === "reviewing").length;

  /* Pinned above the record. The reviewer should never have to read the whole
     page to find out what is being asked of them. */
  const todo = [];
  if (v === "unverified")
    todo.push("Verification has not been started — nothing this vendor lists can go live.");
  if (v === "pending") todo.push("Verification is in review — their listings are still blocked.");
  if (v === "rejected") todo.push("This vendor is rejected. Their listings cannot be published.");
  if (!val(tax.gst)) todo.push("No GSTIN on file — required before they can invoice through us.");
  if (com.margin_rate === null || com.margin_rate === undefined)
    todo.push("No discount agreed — their prices are checked against the platform default discount off the list price.");
  if (bankEmpty) todo.push("No bank details on file — they cannot be paid out.");
  if (openReviews > 0)
    todo.push(
      `${openReviews} submission${openReviews === 1 ? " is" : "s are"} waiting on a decision.`
    );

  const openApproval = (approvalId) => {
    if (!approvalId) return;
    navigate(`/dashboard/productapproval/${approvalId}`);
  };

  return (
    <div className={`${ops.root} ${css.page}`}>
      <BackLink navigate={navigate} />

      {/* ── who is this ───────────────────────────────────────────────── */}
      <div className={css.idHead}>
        <div className={css.idLeft}>
          <Tile name={p.name || p.company} src={p.logo} />
          <div style={{ minWidth: 0 }}>
            <div className={css.idName}>
              <h1>{p.name || p.company || "Unnamed vendor"}</h1>
              <span className={css.vid}>#{p.id ?? id}</span>
              <VerificationBadge status={v} />
            </div>
            <p className={css.metaRow}>
              {p.company && p.company !== p.name && <span>{p.company}</span>}
              {p.owner_email && (
                <span>
                  <Mail />
                  <a href={`mailto:${p.owner_email}`}>{p.owner_email}</a>
                </span>
              )}
              {p.phone && (
                <span>
                  <Phone />
                  {p.phone}
                </span>
              )}
              {p.website && (
                <span>
                  <Globe />
                  <a href={p.website} target="_blank" rel="noreferrer noopener">
                    {String(p.website).replace(/^https?:\/\//, "")}
                  </a>
                </span>
              )}
              <span>
                <Cal />
                Joined {date(p.created_at) || "—"}
              </span>
              {d.verification?.verified_at && v === "verified" && (
                <span>Verified {ago(d.verification.verified_at)}</span>
              )}
            </p>
          </div>
        </div>

        <div className={`${ops.actions} ${css.detailActions}`}>
          {v !== "verified" && (
            <button type="button" disabled={busy} className={`${ops.btnOk} ${css.btnGo}`} onClick={() => setConfirming("verified")}>
              Mark verified
            </button>
          )}
          {/* Available from EVERY state except "already in review", including
              verified: pulling a cleared vendor back for a second look is a
              normal thing to need, and without it the only way back was to
              reject them outright — which is visible to the vendor and reads
              as a verdict rather than a question. */}
          {v !== "pending" && (
            <button type="button" disabled={busy} className={`${ops.btn} ${css.btnPlain}`} onClick={() => setConfirming("pending")}>
              Move to review
            </button>
          )}
          {v !== "rejected" && (
            <button
              type="button"
              disabled={busy}
              className={`${ops.btnDanger} ${css.btnStop}`}
              onClick={() => decide("rejected")}
            >
              Reject
            </button>
          )}
        </div>
      </div>

      <Dialog
        open={Boolean(confirming)}
        onClose={busy ? undefined : () => setConfirming(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{CONFIRM_COPY[confirming]?.title}</DialogTitle>
        <DialogContent>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: "#4A5568" }}>
            {CONFIRM_COPY[confirming]?.body}
          </p>
        </DialogContent>
        <DialogActions>
          <MuiButton disabled={busy} onClick={() => setConfirming(null)}>
            Cancel
          </MuiButton>
          <MuiButton
            variant="contained"
            disabled={busy}
            onClick={async () => {
              const next = confirming;
              setConfirming(null);
              await decide(next);
            }}
          >
            {busy ? "Working…" : CONFIRM_COPY[confirming]?.action}
          </MuiButton>
        </DialogActions>
      </Dialog>

      {error && d && <ErrorCard message={error} onRetry={load} />}

      {asking === "rejected" && (
        <div className={`${ops.card} ${ops.askBox}`}>
          <b>Why are you rejecting this vendor?</b>
          <p className={ops.sub}>They see this. Be specific.</p>
          <textarea
            className={ops.textarea}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. GSTIN does not match the registered company name"
            aria-label="Reason for rejection"
          />
          <div className={ops.actions}>
            <button
              type="button"
              className={`${ops.btnDanger} ${css.btnStop}`}
              disabled={note.trim().length < 5 || busy}
              onClick={() => decide("rejected")}
            >
              Reject vendor
            </button>
            <button type="button" className={`${ops.btn} ${css.btnPlain}`} onClick={() => setAsking(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {d.verification?.note && v === "rejected" && (
        <div className={`${ops.card} ${ops.noteBox}`}>
          <b>Reason on file</b>
          <p>{d.verification.note}</p>
        </div>
      )}

      {/* ── what is outstanding ───────────────────────────────────────── */}
      {todo.length > 0 && (
        <div className={`${ops.card} ${ops.attention}`}>
          <b>Needs attention</b>
          <ul>
            {todo.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
        </div>
      )}

      {/* ── what do they sell, at a glance ────────────────────────────── */}
      <div className={`${ops.kpis} ${css.kpiStrip}`}>
        <Kpi
          value={owned.length + resold.length}
          label={`Products · ${owned.length} owned · ${resold.length} resold`}
          tone="toneInfo"
          icon={IcoStore}
        />
        <Kpi
          value={live + resoldLive}
          label="Live in the marketplace"
          tone="toneOk"
          icon={IcoCheck}
        />
        <Kpi
          value={openReviews}
          label="Submissions awaiting review"
          tone={openReviews ? "toneWarn" : ""}
          icon={IcoInbox}
        />
        <Kpi
          value={com.margin_rate === null || com.margin_rate === undefined ? "—" : pct(com.margin_rate)}
          label="Agreed discount off list"
          tone={com.margin_rate === null || com.margin_rate === undefined ? "toneDanger" : "toneOk"}
          icon={IcoWarn}
        />
      </div>

      {/* ── THE RECORD, AS SECTIONS ──────────────────────────────────────
          Everything below the headline numbers is an expandable section with
          a header that says what is inside and whether it needs attention, so
          the page can be read closed and opened where the question is. */}
      <div className={css.secBar}>
        <b>Vendor record</b>
        <button
          type="button"
          className={css.secBarBtn}
          onClick={() =>
            setOpen(
              Object.fromEntries(SECTION_IDS.map((k) => [k, !allOpen])),
            )
          }
        >
          {allOpen ? "Collapse all" : "Expand all"}
        </button>
      </div>

      {/* 1 ── profile details: tax + banking ─────────────────────────── */}
      <VendorSection
        id="profile"
        icon="profile"
        title="Profile details"
        summary={[
          taxEmpty ? "No tax registration" : val(tax.gst) ? `GSTIN ${tax.gst}` : "Tax registration partial",
          bankEmpty ? "no bank details" : `${val(bank.bank) || "Bank"} ${val(bank.account) || ""}`.trim(),
        ].join(" · ")}
        tone={taxEmpty || bankEmpty ? "warn" : "ok"}
        meta={
          <>
            <span className={`${css.secPill} ${taxEmpty ? css.secPill_warn : css.secPill_ok}`}>
              {taxEmpty ? "tax missing" : "tax on file"}
            </span>
            <span className={`${css.secPill} ${bankEmpty ? css.secPill_warn : css.secPill_ok}`}>
              {bankEmpty ? "bank missing" : "bank on file"}
            </span>
          </>
        }
        open={Boolean(openSecs.profile)}
        onToggle={() => toggleSec("profile")}
      >
        <div className={css.twoCol}>
          <section className={ops.card}>
            <h3>
              Tax registration
              <span className={`${css.hdrFlag} ${taxEmpty ? css.hdrFlagWarn : css.hdrFlagOk}`}>
                {taxEmpty ? "nothing on file" : val(tax.gst) ? "gstin on file" : "partial"}
              </span>
            </h3>
            {taxEmpty ? (
              <Gap
                icon={<Receipt />}
                title="No tax registration submitted"
                body="This vendor has not given us a GSTIN, PAN or CIN. We cannot raise a
                      compliant invoice on their behalf, and verification should not be
                      granted without it."
                email={p.owner_email}
                copied={copied === "tax"}
                onCopy={() => copy(p.owner_email, "tax")}
              />
            ) : (
              <dl className={css.fields}>
                {TAX_FIELDS.map(([key, label, mono]) => (
                  <FieldRow key={key} label={label} value={val(tax[key])} mono={mono} />
                ))}
              </dl>
            )}
          </section>

          <section className={ops.card}>
            <h3>
              Banking
              <span className={`${css.hdrFlag} ${bankEmpty ? css.hdrFlagWarn : css.hdrFlagOk}`}>
                {bankEmpty ? "nothing on file" : "on file"}
              </span>
            </h3>
            {bankEmpty ? (
              <Gap
                icon={<Bank />}
                title="No payout details on file"
                body="This vendor has never submitted an account holder, account number,
                      IFSC or SWIFT. Nothing can be paid out to them until they do — most
                      vendors reach this point without filling it in, so it is worth
                      chasing before their first sale rather than after."
                email={p.owner_email}
                copied={copied === "bank"}
                onCopy={() => copy(p.owner_email, "bank")}
              />
            ) : (
              <dl className={css.fields}>
                {BANK_FIELDS.map(([key, label, mono]) => (
                  <FieldRow key={key} label={label} value={val(bank[key])} mono={mono} />
                ))}
                <dt>&nbsp;</dt>
                <dd>
                  <span className={css.subtle} style={{ fontSize: 11.5 }}>
                    The account number is masked by the API and is never sent in full to
                    this screen.
                  </span>
                </dd>
              </dl>
            )}
          </section>
        </div>
        <BooksVendorCard vendorId={id} books={d.books} onChanged={load} />
      </VendorSection>

      {/* 2 ── commercial agreement + its change history ──────────────── */}
      <VendorSection
        id="agreement"
        icon="agreement"
        title="Commercial agreement"
        summary={
          marginSet
            ? `Negotiated · ${com.margin_rate !== null && com.margin_rate !== undefined ? `${pct(com.margin_rate)} off the list price` : `${money(com.margin_flat_cut)} off list per unit`} · with change history`
            : "Nothing agreed — the platform default rate applies · with change history"
        }
        tone={marginSet ? "ok" : "warn"}
        meta={
          <span className={`${css.secPill} ${marginSet ? css.secPill_ok : css.secPill_warn}`}>
            {marginSet ? "negotiated" : "platform default"}
          </span>
        }
        open={Boolean(openSecs.agreement)}
        onToggle={() => toggleSec("agreement")}
      >
        <MarginAgreement vendorId={Number(id)} onChange={load} embedded />
      </VendorSection>

      {/* 3 ── listings: what they own and what they resell ───────────── */}
      <VendorSection
        id="listings"
        icon="listings"
        title="Listings"
        summary={
          owned.length + resold.length === 0
            ? "Nothing listed or resold yet"
            : `${owned.length} owned${resold.length ? ` · resells ${resold.length}: ${resold.map((r) => r.name).join(", ")}` : ""}`
        }
        tone={owned.length + resold.length ? "info" : "neutral"}
        meta={
          <>
            <span className={`${css.secPill} ${css.secPill_info}`}>
              {owned.length + resold.length}
            </span>
            {listings.some((l) => l.has_open_approval) && (
              <span className={`${css.secPill} ${css.secPill_warn}`}>review waiting</span>
            )}
          </>
        }
        open={Boolean(openSecs.listings)}
        onToggle={() => toggleSec("listings")}
      >
        <div className={css.tableWrap}>
          <table className={ops.table}>
            <thead>
              <tr>
                <SortTh
                  id="name"
                  label="Product"
                  sort={lSort.key}
                  dir={lSort.dir}
                  onSort={(k) => setLSort(nextSort(lSort, k, "asc"))}
                />
                <Th label="Role" />
                <SortTh
                  id="status"
                  label="Status"
                  sort={lSort.key}
                  dir={lSort.dir}
                  onSort={(k) => setLSort(nextSort(lSort, k, "asc"))}
                />
                <Th label="Their plans" />
                <SortTh
                  id="updated"
                  label="Last updated"
                  sort={lSort.key}
                  dir={lSort.dir}
                  onSort={(k) => setLSort(nextSort(lSort, k, "desc"))}
                />
                <Th label="Review" />
              </tr>
            </thead>
            <tbody>
              {listings.length === 0 && (
                <tr>
                  <td colSpan={6} className={ops.empty}>
                    This vendor has not listed or resold anything yet.
                  </td>
                </tr>
              )}
              {listings.map((l) => {
                /* Three states, three affordances. An approval id on its own
                   is NOT an open review — the API falls back to the newest
                   closed one so a decision stays readable — so the live
                   "review this" wording is gated on has_open_approval, and a
                   listing with no approval at all is not a link. */
                const openable = Boolean(l.approval_id);
                const pendingUs = l.has_open_approval;
                return (
                  <tr
                    key={`${l.role}-${l.id}`}
                    className={openable ? `${ops.row} ${css.linkRow}` : undefined}
                    onClick={openable ? () => openApproval(l.approval_id) : undefined}
                  >
                    <td>
                      {openable ? (
                        <button
                          type="button"
                          className={css.cellBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            openApproval(l.approval_id);
                          }}
                        >
                          {l.name}
                        </button>
                      ) : (
                        <span className={css.deadCell}>{l.name}</span>
                      )}
                    </td>
                    <td>
                      <span className={`${css.roleTag} ${l.role === "own" ? css.roleOwn : css.roleResell}`}>
                        {l.role === "own" ? "Owns it" : "Resells it"}
                      </span>
                    </td>
                    <td>
                      <span className={`${ops.badge} ${listingTone(l.status)}`}>
                        {l.status || "—"}
                      </span>
                    </td>
                    <td className={ops.nowrap}>
                      {l.role === "own" ? (
                        <span className={css.subtle}>their listing</span>
                      ) : (
                        <span className={css.stack}>
                          <span>
                            {l.plans_priced} priced
                          </span>
                          <small>{l.plans_live} live</small>
                        </span>
                      )}
                    </td>
                    <td className={ops.nowrap}>
                      <span className={css.stack}>
                        <span>{date(l.updated_at) || "—"}</span>
                        {l.updated_at && <small>{ago(l.updated_at)}</small>}
                      </span>
                    </td>
                    <td>
                      {pendingUs ? (
                        <span className={css.openHint}>
                          Review this <External />
                        </span>
                      ) : openable ? (
                        <span className={css.histHint}>
                          See what we decided
                          {l.approval_status ? ` (${labelFor(l.approval_status)})` : ""}{" "}
                          <External />
                        </span>
                      ) : (
                        <span className={css.noHint}>never submitted</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </VendorSection>

      {/* 4 ── submission history: prices, content and catalogue requests ─ */}
      <VendorSection
        id="submissions"
        icon="submissions"
        title="Submission history"
        summary={
          approvals.length
            ? `${approvals.length} submission${approvals.length === 1 ? "" : "s"} — prices, content and catalogue requests`
            : "Nothing has ever been submitted for review"
        }
        tone={openReviews ? "warn" : approvals.length ? "info" : "neutral"}
        meta={
          <>
            <span className={`${css.secPill} ${approvals.length ? css.secPill_info : ""}`}>
              {approvals.length}
            </span>
            {openReviews > 0 && (
              <span className={`${css.secPill} ${css.secPill_warn}`}>
                {openReviews} waiting
              </span>
            )}
          </>
        }
        open={Boolean(openSecs.submissions)}
        onToggle={() => toggleSec("submissions")}
      >
        <div className={css.tableWrap}>
          <table className={ops.table}>
            <thead>
              <tr>
                <SortTh
                  id="sku"
                  label="Product"
                  sort={aSort.key}
                  dir={aSort.dir}
                  onSort={(k) => setASort(nextSort(aSort, k, "asc"))}
                />
                <SortTh
                  id="change"
                  label="What changed"
                  sort={aSort.key}
                  dir={aSort.dir}
                  onSort={(k) => setASort(nextSort(aSort, k, "asc"))}
                />
                <SortTh
                  id="outcome"
                  label="Outcome"
                  sort={aSort.key}
                  dir={aSort.dir}
                  onSort={(k) => setASort(nextSort(aSort, k, "asc"))}
                />
                <SortTh
                  id="submitted"
                  label="Submitted"
                  sort={aSort.key}
                  dir={aSort.dir}
                  onSort={(k) => setASort(nextSort(aSort, k, "desc"))}
                />
                <Th label="Review" />
              </tr>
            </thead>
            <tbody>
              {approvals.length === 0 && (
                <tr>
                  <td colSpan={5} className={ops.empty}>
                    Nothing has ever been submitted for review.
                  </td>
                </tr>
              )}
              {approvals.map((a) => {
                // A catalogue request opens in the requests queue; a price or
                // content submission opens its review.
                const go = () =>
                  a.kind === "request"
                    ? navigate(`/dashboard/catalog_proposals?focus=${a.proposal_id}`)
                    : openApproval(a.id);
                return (
                  <tr
                    key={a.id}
                    className={`${ops.row} ${css.linkRow} ${a.over_cap ? ops.rowDanger : ""}`}
                    onClick={go}
                  >
                    <td>
                      <button
                        type="button"
                        className={css.cellBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          go();
                        }}
                      >
                        {a.sku_name}
                      </button>
                    </td>
                    <td>
                      <span className={css.stack}>
                        <span>{a.change_type || <span className={css.subtle}>—</span>}</span>
                        {a.summary && <small>{a.summary}</small>}
                      </span>
                      {a.over_cap && (
                        <span className={`${ops.badge} ${ops.danger}`}>over cap</span>
                      )}
                    </td>
                    <td>
                      <span className={css.stack}>
                        <span className={`${ops.badge} ${approvalTone(a.status)}`}>
                          {labelFor(a.status)}
                        </span>
                        {a.decision_note && <small>{a.decision_note}</small>}
                      </span>
                    </td>
                    <td className={ops.nowrap}>
                      <span className={css.stack}>
                        <span>{date(a.created_at) || "—"}</span>
                        {a.created_at && <small>{ago(a.created_at)}</small>}
                      </span>
                    </td>
                    <td>
                      {/* Same distinction as the listings table: something we
                          still owe a decision on reads as work, a settled one
                          reads as a record. */}
                      {OPEN_APPROVAL.has(a.status) ? (
                        <span className={css.openHint}>
                          Review this <External />
                        </span>
                      ) : (
                        <span className={css.histHint}>
                          See what we decided <External />
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </VendorSection>

      {/* 5–8 ── what this vendor actually does ──────────────────────────
          Loaded on first open: a vendor with a thousand subscriptions should
          not make this page slow. */}
      <VendorActivity
        vendorId={id}
        scope="payouts"
        title="Payouts"
        get={activityGet}
        open={Boolean(openSecs.payouts)}
        onToggle={() => toggleSec("payouts")}
      />
      <VendorActivity
        vendorId={id}
        scope="orders"
        title="Orders on their prices"
        get={activityGet}
        open={Boolean(openSecs.orders)}
        onToggle={() => toggleSec("orders")}
      />
      <VendorActivity
        vendorId={id}
        scope="subscriptions"
        title="Subscriptions on their prices"
        get={activityGet}
        open={Boolean(openSecs.subscriptions)}
        onToggle={() => toggleSec("subscriptions")}
      />
      <VendorActivity
        vendorId={id}
        scope="customers"
        title="Customers buying from them"
        get={activityGet}
        open={Boolean(openSecs.customers)}
        onToggle={() => toggleSec("customers")}
      />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */

function BackLink({ navigate }) {
  return (
    <button type="button" className={`${ops.back} ${css.backBtn}`} onClick={() => navigate("/dashboard/vendors")}>
      ‹ All vendors
    </button>
  );
}

function FieldRow({ label, value, mono }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>
        <Value mono={mono}>{value}</Value>
      </dd>
    </>
  );
}

/** Section-level empty state: a known gap with something to do about it. */
function Gap({ icon, title, body, email, onCopy, copied }) {
  return (
    <div className={css.sectionGap}>
      <span className={css.sectionGapIco}>{icon}</span>
      <div>
        <b>{title}</b>
        <p>{body}</p>
        <div className={css.act}>
          {email && (
            <>
              <a className={css.ghostBtn} href={`mailto:${email}`}>
                <Mail />
                Email the vendor
              </a>
              <button type="button" className={css.ghostBtn} onClick={onCopy}>
                <Copy />
                {copied ? "Copied" : "Copy address"}
              </button>
            </>
          )}
          {!email && (
            <span className={css.noHint}>
              <FileOff /> No owner email either — nothing to chase this with.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/** Approval states read as jargon in the DB; say them in English. */
const labelFor = (s) =>
  ({
    reviewing: "in review",
    changes_requested: "changes requested",
    cancelled: "cancelled",
    approved: "approved",
    rejected: "rejected",
  })[s] || s;

/** Click a new column → its natural direction. Click the active one → flip. */
const nextSort = (cur, key, first) =>
  cur.key === key ? { key, dir: cur.dir === "asc" ? "desc" : "asc" } : { key, dir: first };

const listingTone = (s) => {
  const k = String(s || "").toLowerCase();
  if (k === "active") return ops.ok;
  if (k === "pending_approval") return ops.warn;
  if (k === "draft") return ops.muted;
  return ops.muted;
};

const approvalTone = (s) => {
  if (s === "approved") return ops.ok;
  if (s === "rejected") return ops.danger;
  if (s === "reviewing") return ops.warn;
  if (s === "changes_requested") return ops.info;
  return ops.muted;
};

export { STATUS_LABEL };
